import {
  ActionPlan,
  OperationsSnapshot,
  RunAggregateProjectionV2,
  RunDefinitionProjection,
  RunEvent,
  RunEventBatch,
  WebTestRun,
  type ActionPlan as ActionPlanValue,
  type RunAggregateProjectionV2 as RunAggregateProjectionValue,
  type WebTestRun as WebTestRunValue
} from '@blume/web-test-model'
import { z } from 'zod'

const ListRunsResponse = z.looseObject({
  runs: z.array(WebTestRun),
  pagination: z.looseObject({ limit: z.number(), offset: z.number() })
})

const RunResponse = z.looseObject({ run: WebTestRun })
const EventsResponse = RunEventBatch
const AggregatesResponse = z.looseObject({ aggregates: z.array(RunAggregateProjectionV2) })
const CandidateResponse = z.looseObject({
  candidate: ActionPlan,
  diff: z.unknown().optional()
})
export type RunPage = z.infer<typeof ListRunsResponse>
export type CandidateReview = z.infer<typeof CandidateResponse>

export interface DashboardApi {
  listRuns(input?: ListRunsInput): Promise<WebTestRunValue[]>
  listRunsPage(input?: ListRunsInput): Promise<RunPage>
  listAggregates(
    kind: RunAggregateProjectionValue['kind'],
    limit?: number
  ): Promise<RunAggregateProjectionValue[]>
  getAggregate(
    kind: RunAggregateProjectionValue['kind'],
    key: string
  ): Promise<RunAggregateProjectionValue | undefined>
  getOperations(): Promise<z.infer<typeof OperationsSnapshot>>
  getRun(runId: string): Promise<WebTestRunValue>
  getRunDefinition(runId: string): Promise<z.infer<typeof RunDefinitionProjection>>
  getEvents(runId: string, afterSequence?: number): Promise<z.infer<typeof RunEvent>[]>
  cancelRun(runId: string): Promise<WebTestRunValue>
  getCandidatePlan(runId: string): Promise<ActionPlanValue>
  getCandidateReview(runId: string): Promise<CandidateReview>
  artifactUrl(runId: string, artifactId: string): string
}

export interface ListRunsInput {
  limit?: number
  offset?: number
  status?: string
  classification?: string
  origin?: string
  target?: string
  scenarioId?: string
  suiteRunId?: string
  branch?: string
  pullRequest?: number
  query?: string
}

export class DashboardApiError extends Error {
  readonly status: number
  readonly code: string

  constructor(status: number, code: string, message: string) {
    super(message)
    this.name = 'DashboardApiError'
    this.status = status
    this.code = code
  }
}

export function buildDashboardSignInUrl(baseUrl: string, returnTo: string): string {
  const base = baseUrl.replace(/\/+$/, '')
  const endpoint = new URL(base ? `${base}/auth/sign-in` : '/auth/sign-in', returnTo)
  endpoint.searchParams.set('returnTo', returnTo)
  return endpoint.toString()
}

export function createDashboardApi(
  baseUrl: string,
  fetcher: typeof fetch = globalThis.fetch.bind(globalThis)
): DashboardApi {
  const base = baseUrl.replace(/\/+$/, '')

  async function request(path: string, init?: RequestInit): Promise<unknown> {
    const response = await fetcher(`${base}${path}`, {
      ...init,
      credentials: 'include',
      headers: {
        accept: 'application/json',
        ...(init?.body ? { 'content-type': 'application/json' } : {}),
        ...init?.headers
      }
    })
    const payload = (await response.json().catch(() => ({}))) as {
      error?: { code?: string; message?: string }
    }
    if (!response.ok) {
      throw new DashboardApiError(
        response.status,
        payload.error?.code ?? 'request_failed',
        payload.error?.message ?? `Verifier returned HTTP ${response.status}`
      )
    }
    return payload
  }

  return {
    async listRuns(input = {}) {
      return (await this.listRunsPage(input)).runs
    },
    async listRunsPage(input = {}) {
      const query = new URLSearchParams()
      if (input.limit !== undefined) query.set('limit', String(input.limit))
      if (input.offset !== undefined) query.set('offset', String(input.offset))
      if (input.status) query.set('status', input.status)
      if (input.classification) query.set('classification', input.classification)
      if (input.origin) query.set('origin', input.origin)
      if (input.target) query.set('target', input.target)
      if (input.scenarioId) query.set('scenarioId', input.scenarioId)
      if (input.suiteRunId) query.set('suiteRunId', input.suiteRunId)
      if (input.branch) query.set('branch', input.branch)
      if (input.pullRequest !== undefined) query.set('pullRequest', String(input.pullRequest))
      if (input.query) query.set('q', input.query)
      const suffix = query.size > 0 ? `?${query}` : ''
      return ListRunsResponse.parse(await request(`/v1/runs${suffix}`))
    },
    async listAggregates(kind, limit = 100) {
      return AggregatesResponse.parse(
        await request(
          `/v1/aggregates/${encodeURIComponent(kind)}?limit=${encodeURIComponent(limit)}`
        )
      ).aggregates
    },
    async getAggregate(kind, key) {
      const query = new URLSearchParams({ limit: '1', key })
      return AggregatesResponse.parse(
        await request(`/v1/aggregates/${encodeURIComponent(kind)}?${query}`)
      ).aggregates[0]
    },
    async getOperations() {
      return OperationsSnapshot.parse(await request('/v1/operations'))
    },
    async getRun(runId) {
      return RunResponse.parse(await request(`/v1/runs/${encodeURIComponent(runId)}`)).run
    },
    async getRunDefinition(runId) {
      return z
        .looseObject({ definition: RunDefinitionProjection })
        .parse(await request(`/v1/runs/${encodeURIComponent(runId)}/definition`)).definition
    },
    async getEvents(runId, afterSequence = -1) {
      return EventsResponse.parse(
        await request(
          `/v1/runs/${encodeURIComponent(runId)}/events?after=${encodeURIComponent(afterSequence)}`
        )
      ).events
    },
    async cancelRun(runId) {
      return RunResponse.parse(
        await request(`/v1/runs/${encodeURIComponent(runId)}/cancel`, { method: 'POST' })
      ).run
    },
    async getCandidatePlan(runId) {
      return (await this.getCandidateReview(runId)).candidate
    },
    async getCandidateReview(runId) {
      return CandidateResponse.parse(
        await request(`/v1/runs/${encodeURIComponent(runId)}/candidate`)
      )
    },
    artifactUrl(runId, artifactId) {
      return `${base}/v1/runs/${encodeURIComponent(runId)}/artifacts/${encodeURIComponent(artifactId)}`
    }
  }
}
